// search product init — Dropdown / Modal (click) + Results (URL predicate).
//
// Real, correctly-typed call sites that map each widget's own named callback
// bundle (Dropdown/Modal/ResultsCallbacks) into that widget's POSITIONAL
// create() params (PLAN.md search signatures; arg order
// verified in iter 01). dropdown / modal / results are each isolated in their own
// try/catch so one widget failing doesn't skip the others, and the created
// instance(s) are collected into SearchInitResult for teardown/inspection.
//
// ANALYTICS LINK (no wiring needed): search auto-integrates with analytics via the
// shared `window.raventicLayer` — CommonConfig `analyticsEventsTarget` defaults to
// `'raventicLayer'`, which initAnalytics's RaventicLayerCollector drains — so no
// explicit `eventPusher` has to be threaded between search and analytics.
//
// LATE ACTIVATION (iter 13): results' `activateWhen` may also be async or a trigger;
// then startActivation() (../activation-runtime.ts) draws it later with the SAME
// create() call, once only, and reports through `onActivation` (not in
// SearchInitResult). Sync predicates behave exactly as before.
//
// EARLY CLICKS (iter 14, iter 22): boot() installs document-level guards (../click-guard.ts)
// for the dropdown and the modal before waiting for the DOM, which swallow activations of
// their elements until the widget exists. Each widget's step releases its guard right BEFORE that widget's
// create() (the guard's document-capture listeners would otherwise kill the events before the
// widget's own element listeners see them) and then open()s — one animation frame later —
// every widget that was clicked while guarded (the dropdown also: focused by the user with the
// focus still holding).
//
// SCRIPT START (iter 21): boot() calls prepareSearch() at widget.js execution, before the DOM
// wait, whenever the booted config has a search section. It
//   - evaluates results' `activateWhen` ONCE (the only evaluation of a sync or async predicate
//     this page view — the results step reuses the verdict; a trigger is only recognised, and
//     subscribed after the wait as before). A sync `false` while the document is still loading
//     gets ONE re-check at DOM readiness (a predicate reading the page gets its second chance
//     there); a `false` on an already parsed document stands. So results' predicate runs before
//     the page is parsed and should decide on `ctx.url` (all current ones do);
//   - compiles the `hide` of the dropdown, the modal and — when it will draw now (no predicate,
//     or a sync `true`) — results, and injects them as ONE `<style data-rvn-hide>` into <head>
//     (../hide.ts injectHideStyle), so the native elements those widgets replace never paint.
//     Those widgets then skip withHide() (no double rules). An invalid rule is that widget's
//     error (thrown inside its own try/catch here, exactly as in iter 17: nothing created), and
//     none of its rules are injected; the others' still are. If the injection itself fails,
//     every widget falls back to withHide().
//   - (iter 24) when results will draw (`results.kind === "draw"`), starts its search request
//     right away: `ResultsWidget.prefetch(config)` (sdk-search 5.9) — after boot's
//     applyRuntime(), so the search API URL is configured — with the EXACT config object the
//     results step later hands to create(), together with the handle (create()'s 9th param).
//     The request then runs in parallel with the DOM wait, the shadow CSS download and the
//     other widgets' creates; the widget's first load consumes the in-flight promise when its
//     request matches (else fetches as before; a failed prefetch is handled like a failed load).
//     Never on an experiment's null arm (prepareSearch doesn't run), never for a predicate that said no at script
//     start (a DCL re-check that says yes draws WITHOUT a prefetch), never for async predicates
//     or triggers. prefetch() never throws by contract; if it does anyway (or the widget class
//     has none), it is console.error'd and results draws without the handle.
//
// HIDE (the remaining path, iter 17): a widget whose rules were NOT injected at boot gets its
// `hide` (../hide.ts) compiled and appended to a COPY of its config's `customPageStyles` right
// before create() (withHide), so the SDK injects it with its own page styles — results (async
// predicate / trigger) on create, dropdown / modal on first open; never removed, so it lasts for
// the page view. An invalid rule is that widget's error and nothing is created. Results' `hide`
// is compiled FIRST, at script start, before its predicate is even called (like a carousel's
// before startActivation()): an invalid rule never starts predicate work or a subscription —
// also when the predicate would have said no. The customer's config object is never mutated.

import type { RaventicSdkConfig } from "../config";
import type { ActivationContext } from "../activation";
import { isActivationTrigger } from "../activation";
import type { ActivationReporter } from "../activation-runtime";
import { startActivation } from "../activation-runtime";
import type { IndexGuard } from "../click-guard";
import { replayNextFrame } from "../click-guard";
import { compileHide, injectHideStyle, withHide, withPageStyles } from "../hide";
// Types only: each activation carries its own widget class (`widget`, set by
// defineDropdown/defineModal/defineResults — see ../define.ts), so a customer's
// widget.js bundles only the search widgets its config actually defines.
import type {
  DropdownWidget,
  ModalWidget,
  ResultsWidget,
  ResultsPrefetch,
} from "@raventic/sdk-search-v1";

type SearchConfig = NonNullable<RaventicSdkConfig["search"]>;

// Instance field types are DERIVED from the create() return types (create returns
// `Widget[]` for dropdown/modal and `Promise<Widget>` for results) rather than
// naming an instance type the package may not export — this stays exact once the
// dep resolves without guessing.
export interface SearchInitResult {
  dropdown?: ReturnType<typeof DropdownWidget.create>;
  modal?: ReturnType<typeof ModalWidget.create>;
  results?: Awaited<ReturnType<typeof ResultsWidget.create>>;
  errors: Array<{ widget: "dropdown" | "modal" | "results"; error: unknown }>;
}

// ── Script start (iter 21) ────────────────────────────────────────────────────────────────

// A click widget's `hide` at script start: injected into the boot <style>, or invalid.
export type ClickWidgetPlan = { hideInjected: boolean } | { error: unknown };

// What script start decided for results (see the header).
export type ResultsPlan =
  // No `activateWhen`, or a sync predicate that returned true: draws at DOM readiness.
  // `config` = the config create() gets (set once prepareSearch has injected the boot <style>):
  // the customer's, or a copy with the hide in customPageStyles; `prefetch` = the handle of the
  // search request started at script start with that same object (iteration 24).
  | { kind: "draw"; hideInjected: boolean; config?: ResultsConfigOf; prefetch?: ResultsPrefetch }
  // A sync predicate returned false on an already parsed document: no results this page view.
  | { kind: "skip" }
  // A sync predicate returned false while the document was loading: re-checked ONCE at DOM
  // readiness.
  | { kind: "recheck" }
  // An async predicate, already called: its (pending) answer, for the late pipeline.
  | { kind: "pending"; verdict: unknown }
  // A trigger: subscribed after the DOM wait, exactly as before.
  | { kind: "late" }
  // The predicate threw, or the (now needed) hide rule is invalid: results' boot error.
  | { kind: "error"; error: unknown };

type ResultsConfigOf = NonNullable<SearchConfig["results"]>["config"];

export interface SearchBootPlan {
  dropdown?: ClickWidgetPlan;
  modal?: ClickWidgetPlan;
  results?: ResultsPlan;
  // The `cspNonce` of the search configs (the first one set), for the hide <style>.
  nonce?: string;
  // The boot <style> injection failed (every widget fell back to withHide); boot() records it
  // as `search:hide`.
  hideError?: unknown;
}

const isThenable = (value: unknown): boolean =>
  (typeof value === "object" || typeof value === "function") &&
  value !== null &&
  typeof (value as { then?: unknown }).then === "function";

const nonceOf = (config: unknown): string | undefined => {
  const nonce = (config as { cspNonce?: unknown } | undefined)?.cspNonce;
  return typeof nonce === "string" && nonce !== "" ? nonce : undefined;
};

// Script-start step for the search widgets (see the header). Never throws: every per-widget
// failure lands in the plan (handed to searchPipelines, whose step records it inside that widget's
// try/catch); an injection failure is `hideError`. `doc` = the host document (tests).
export function prepareSearch(cfg: SearchConfig, ctx: ActivationContext, doc: Document = document): SearchBootPlan {
  const plan: SearchBootPlan = {};
  plan.nonce = nonceOf(cfg.dropdown?.config) ?? nonceOf(cfg.modal?.config) ?? nonceOf(cfg.results?.config);
  // The compiled rules to inject, per widget (only valid ones).
  const css: Array<{ widget: "dropdown" | "modal" | "results"; css: string }> = [];
  const compile = (widget: "dropdown" | "modal" | "results", hide: Parameters<typeof compileHide>[0]): ClickWidgetPlan => {
    try {
      const rules = compileHide(hide);
      if (rules !== "") css.push({ widget, css: rules });
      return { hideInjected: rules !== "" };
    } catch (error) {
      return { error };
    }
  };

  if (cfg.dropdown) plan.dropdown = compile("dropdown", cfg.dropdown.hide);
  if (cfg.modal) plan.modal = compile("modal", cfg.modal.hide);
  if (cfg.results) {
    // `hide` first (see HIDE in the header): an invalid rule never runs the predicate.
    let rules: string | undefined;
    try {
      rules = compileHide(cfg.results.hide);
    } catch (error) {
      plan.results = { kind: "error", error };
    }
    if (rules !== undefined) {
      const verdict = resultsVerdict(cfg.results.activateWhen, ctx, doc);
      if (verdict.kind === "draw" && rules !== "") css.push({ widget: "results", css: rules });
      plan.results = verdict.kind === "draw" ? { kind: "draw", hideInjected: rules !== "" } : verdict;
    }
  }

  if (css.length > 0) {
    try {
      injectHideStyle(
        css.map((c) => c.css).join("\n"),
        css.map((c) => c.widget),
        plan.nonce,
        doc,
      );
    } catch (error) {
      plan.hideError = error;
      // Fall back: every widget appends its rules to customPageStyles as in iteration 17.
      for (const widget of ["dropdown", "modal", "results"] as const) {
        const p = plan[widget];
        if (p && "hideInjected" in p) p.hideInjected = false;
      }
    }
  }

  // Results will draw: fix the config create() gets, and start its request now (iteration 24).
  const results = cfg.results;
  if (results && plan.results?.kind === "draw") {
    const draw = plan.results;
    try {
      draw.config = draw.hideInjected ? results.config : withHide(results.config, results.hide);
      if (typeof results.widget?.prefetch === "function") {
        draw.prefetch = results.widget.prefetch(draw.config);
      }
    } catch (error) {
      // Not a boot error: results still draws, just without the head start.
      console.error("[@raventic/sdk] search results prefetch failed", error);
    }
  }
  return plan;
}

// Results' activation at script start: the ONE evaluation of a predicate (see the header).
function resultsVerdict(
  when: NonNullable<SearchConfig["results"]>["activateWhen"],
  ctx: ActivationContext,
  doc: Document,
): ResultsPlan {
  // Falsy (absent, null, false) → the default, true — startActivation()'s rule.
  if (!when) return { kind: "draw", hideInjected: false };
  if (isActivationTrigger(when)) return { kind: "late" };
  let verdict: unknown;
  try {
    verdict = when(ctx);
  } catch (error) {
    return { kind: "error", error };
  }
  if (isThenable(verdict)) {
    // Handled here already, so a rejection before the DOM wait isn't reported as unhandled by
    // the browser; the late pipeline attaches its own handler to the same promise later.
    Promise.resolve(verdict).catch(() => undefined);
    return { kind: "pending", verdict };
  }
  if (verdict) return { kind: "draw", hideInjected: false };
  return doc.readyState === "loading" ? { kind: "recheck" } : { kind: "skip" };
}

// ── After the DOM wait ──────────────────────────────────────────────────────────────────────

// One step per configured widget (iter 23): boot() runs each in its own pipeline — after that
// widget's readiness (today: the shared DOM wait) — so no widget's init waits for another's. A
// step never throws: its failure is recorded in `result.errors` and reported to `onError`
// (boot's BootResult.errors), and console.error'd. Building the steps reads the config only;
// nothing runs until a step is called.
export type SearchWidget = "dropdown" | "modal" | "results";
export type SearchSteps = { [W in SearchWidget]?: () => void | Promise<void> };

export function searchPipelines(
  cfg: SearchConfig,
  // The PAGE context, read when a step runs (boot builds it at DOM readiness, so a <head>
  // script's history.replaceState is reflected) — not prepareSearch()'s script-start one.
  ctx: () => ActivationContext,
  report: ActivationReporter,
  // boot()'s early-click guards, when it installed them (each released by its step, right
  // before its widget's create()).
  guards: { dropdown?: IndexGuard; modal?: IndexGuard },
  // prepareSearch()'s plan (iter 21); without one, everything is decided by the steps.
  early: SearchBootPlan,
  // Filled in place: the created instances + the per-widget failures.
  result: SearchInitResult,
  onError: (widget: SearchWidget, error: unknown) => void = () => undefined,
): SearchSteps {
  const steps: SearchSteps = {};
  const fail = (widget: SearchWidget, message: string, error: unknown): void => {
    result.errors.push({ widget, error });
    onError(widget, error);
    console.error(`[@raventic/sdk] ${message}`, error);
  };

  // dropdown (click): create() binds click-to-open internally; synchronous.
  if (cfg.dropdown) {
    const dropdown = cfg.dropdown;
    steps.dropdown = () => {
      try {
        const { widget, activateOn, callbacks, hide } = dropdown;
        const cb = callbacks ?? {};
        // Release FIRST (see the header); it never throws and boot() releases again in a
        // finally, so a create() (or `hide`) failure below can't leave the guard armed either.
        const activated = guards.dropdown?.release() ?? new Set<number>();
        const config = clickConfig(dropdown.config, hide, early.dropdown);
        // DropdownWidget.create(selectors, config, onOpen?, onProductClick?, errorHandler?,
        //   resultHandler?, eventPusher?, onClose?, onContentClick?,
        //   onAssistantSessionStart?, onAssistantContact?, onSubmit?)
        // The custom card (config.card/cardOptions) rides inside `config` — forwarded as-is.
        result.dropdown = widget.create(
          activateOn,
          config,
          cb.onOpen,
          cb.onProductClick,
          cb.errorHandler,
          cb.resultHandler,
          cb.eventPusher,
          cb.onClose,
          cb.onContentClick,
          cb.onAssistantSessionStart,
          cb.onAssistantContact,
          cb.onSubmit,
        );
        if (activated.size > 0) openActivated(result.dropdown, activated, "search dropdown");
      } catch (error) {
        fail("dropdown", "search dropdown init failed", error);
      }
    };
  }

  // modal (click): create() binds click-to-open internally; synchronous.
  if (cfg.modal) {
    const modal = cfg.modal;
    steps.modal = () => {
      try {
        const { widget, activateOn, callbacks, hide } = modal;
        const cb = callbacks ?? {};
        // Release FIRST, like the dropdown's (boot() releases again in a finally). Only the
        // LATEST early activation is replayed: every index is its own full-screen modal.
        guards.modal?.release();
        const latest = guards.modal?.lastActivation() ?? null;
        const activated = new Set<number>(latest === null ? [] : [latest]);
        const config = clickConfig(modal.config, hide, early.modal);
        // ModalWidget.create(selector, config, onOpen?, onProductClick?, errorHandler?,
        //   resultHandler?, eventPusher?, onClose?, onContentClick?)
        // The custom card (config.card/cardOptions) and promo-content item/block
        // (config.promoContentItem/promoContentBlock + *Options, search 5.2.0) ride
        // inside `config` — forwarded as-is.
        result.modal = widget.create(
          activateOn,
          config,
          cb.onOpen,
          cb.onProductClick,
          cb.errorHandler,
          cb.resultHandler,
          cb.eventPusher,
          cb.onClose,
          cb.onContentClick,
        );
        if (activated.size > 0) openActivated(result.modal, activated, "search modal");
      } catch (error) {
        fail("modal", "search modal init failed", error);
      }
    };
  }

  // results (page load): gated on the URL predicate (default true); async — it
  // reads the query from the page URL, clears the target, and mounts into it. The
  // predicate's re-check (or a trigger's subscribe) runs INSIDE the try so a throwing customer
  // predicate is recorded as a results error, never anything else's.
  if (cfg.results) {
    const results = cfg.results;
    // ResultsWidget.create(elementSelector, config, onOpen?, onProductClick?,
    //   errorHandler?, resultHandler?, eventPusher?, onContentClick?, prefetch?): Promise<Widget>
    // `prefetch` (search 5.9): the handle of the request prepareSearch() started at script start
    // (iteration 24). The custom card (config.card/cardOptions) and promo-content item/block
    // (config.promoContentItem/promoContentBlock + *Options, search 5.2.0) ride
    // inside `config` — forwarded as-is.
    const create = (config: typeof results.config, prefetch?: ResultsPrefetch) => {
      const { widget, target, callbacks } = results;
      const cb = callbacks ?? {};
      return widget.create(
        target,
        config,
        cb.onOpen,
        cb.onProductClick,
        cb.errorHandler,
        cb.resultHandler,
        cb.eventPusher,
        cb.onContentClick,
        prefetch,
      );
    };
    steps.results = async () => {
      try {
        const plan = early.results;
        // Recorded once: the script-start evaluation threw, or its hide rule is invalid.
        if (plan?.kind === "error") throw plan.error;
        // `hide` compiled up front (see the header): validated even when nothing draws now, and
        // shared by every draw that didn't get its rules injected at boot.
        const hideCss = compileHide(results.hide);
        const lateConfig = withPageStyles(results.config, hideCss);
        const late = {
          widget: "search:results",
          container: results.target,
          containerTimeout: results.containerTimeout,
          onceOnly: true,
          draw: async () => {
            await create(lateConfig);
            return null; // results has no instance id
          },
        };

        let drawNow = false;
        let config = lateConfig;
        let prefetch: ResultsPrefetch | undefined;
        switch (plan?.kind) {
          case "draw":
            drawNow = true;
            // The very object the prefetch was started with (one config for both).
            config = plan.config ?? (plan.hideInjected ? results.config : lateConfig);
            prefetch = plan.prefetch;
            break;
          case "skip":
            break;
          case "pending":
            // The async predicate's answer from script start: drawn later, hide on draw.
            startActivation(results.activateWhen, ctx(), late, report, { verdict: plan.verdict });
            break;
          case "recheck":
            // The ONE re-check of a predicate that said no while the document was loading.
            drawNow = startActivation(results.activateWhen, ctx(), late, report);
            if (drawNow) config = injectLate(results.config, lateConfig, hideCss, early.nonce);
            break;
          default:
            // A trigger ("late"), or no plan: decided here.
            drawNow = startActivation(results.activateWhen, ctx(), late, report);
        }
        if (drawNow) result.results = await create(config, prefetch);
      } catch (error) {
        fail("results", "search results init failed", error);
      }
    };
  }

  return steps;
}

// A click widget's config: as-is when its `hide` went into the boot <style>, else with the
// `hide` appended to customPageStyles (withHide). Throws the script-start compile error (or
// withHide's own) inside the widget's try/catch.
function clickConfig<C extends { customPageStyles?: string }>(
  config: C,
  hide: Parameters<typeof withHide>[1],
  plan: ClickWidgetPlan | undefined,
): C {
  if (plan && "error" in plan) throw plan.error;
  return plan?.hideInjected ? config : withHide(config, hide);
}

// Results drawing after its DOM-readiness re-check: its rules go into a boot-style <style> now
// (same mechanism as at script start); the plain config is then drawn. If that injection fails,
// the rules ride customPageStyles instead (console.error only — the widget still works).
function injectLate<C>(config: C, lateConfig: C, css: string, nonce: string | undefined): C {
  if (css === "") return config;
  try {
    injectHideStyle(css, ["results"], nonce);
    return config;
  } catch (error) {
    console.error("[@raventic/sdk] search results hide injection failed", error);
    return lateConfig;
  }
}

// Replay the early activations as open(): the dropdowns / modals clicked while their guard was
// armed, or (dropdown) focused by the user with that focus still holding (../click-guard.ts
// decides). `create()` returns one Widget per selector, in selector order, so index i of the
// set is widgets[i].
//
// Deferred to the next animation frame (setTimeout 0 where rAF is missing): this runs at
// DOM readiness, while the host header may still be settling (late CSS / fonts / images),
// and open() positions the dropdown ONCE from the input's getBoundingClientRect — a frame
// later the layout is current. Each widget is opened at most once; the callback re-checks
// `opened` (a click after create() may have opened it in between; open() is idempotent
// anyway). The opens are async, so their failures are console.error'd only — the step
// does not wait for them, and BootResult.errors is already filled by then.
//
// The dropdown's open() also seeds the query from the external input's current value
// (sdk-search window.tsx `initialRequest(props.externalInput?.value, …)` on first render), so
// text typed into the focused box before init is searched for at once.
function openActivated(
  widgets: ReadonlyArray<{ open(): void }>,
  activated: ReadonlySet<number>,
  label: string,
): void {
  replayNextFrame(label, () => {
    for (const index of activated) {
      const widget = widgets[index];
      // `opened` is the widget's own (private) flag — read defensively.
      if (!widget || (widget as unknown as { opened?: unknown }).opened === true) continue;
      try {
        widget.open();
      } catch (error) {
        console.error(`[@raventic/sdk] ${label} early open failed`, error);
      }
    }
  });
}
